import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "背景を透明にする方法【画像・動画・PowerPoint別】",
	description:
		"画像・動画・PowerPointで背景を透明にする方法を用途別に解説。AIツールからPowerPointの組み込み機能まで、透過処理の手順を詳しく紹介。",
	publishDate: "2026-03-24",
	slug: "background-transparent-guide",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function BackgroundTransparentGuide() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section>
						<p className="text-gray-700 mb-6">
							「背景を透明にしたい」というニーズは、商品画像・プレゼン資料・動画編集など幅広い場面で生じます。
							ただし、「画像の透過」「PowerPointでの透過」「動画の背景除去」では使うツールや手順がまったく異なります。
							この記事では用途ごとに最適な方法を解説します。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							「背景を透明にする」とはどういう意味か
						</h2>
						<p className="text-gray-700 mb-4">
							画像の背景を透明にするとは、被写体（商品・人物・ロゴ等）の周囲の不要な部分を切り取り、その部分を透明（アルファチャンネル）にすることです。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ul className="space-y-2">
								<li>
									<span className="font-semibold">透明を保存できる形式：</span>
									PNG・GIF・WebP・SVG（JPEGは透明を保存できない）
								</li>
								<li>
									<span className="font-semibold">透明の見た目：</span>
									チェッカー柄（市松模様）で表示されるのが透明の証
								</li>
								<li>
									<span className="font-semibold">用途：</span>
									EC商品画像・プレゼン素材・Webデザイン・動画合成
								</li>
							</ul>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							【画像編】背景を透明にする方法
						</h2>

						<h3 className="text-lg font-semibold mt-6 mb-3">
							方法1：しろぬきや（AI自動・登録不要・無料）
						</h3>
						<p className="text-gray-700 mb-4">
							AIが自動で被写体を検出し、背景を透明にして透過PNG・WebPとして書き出します。
							ブラウザだけで完結するため、インストール不要です。
						</p>
						<div className="bg-indigo-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<p className="font-semibold mb-2">しろぬきやの手順</p>
							<ol className="list-decimal list-inside space-y-1">
								<li>しろぬきや（shironukiya.fucina.casa）にアクセス</li>
								<li>画像をドラッグ＆ドロップまたはファイル選択</li>
								<li>AIが自動で背景を除去</li>
								<li>透過PNG・白背景JPEGを選んでダウンロード</li>
							</ol>
						</div>
						<p className="text-gray-700 mb-4">
							EC出品の場合は「白背景」での出力も選択できます。
							Amazonでは透過PNGをそのままアップロードすると表示が崩れることがありますが、
							しろぬきやの白背景変換機能を使えば問題を回避できます。
						</p>

						<CtaBlock />

						<h3 className="text-lg font-semibold mt-8 mb-3">
							方法2：remove.bg
						</h3>
						<p className="text-gray-700 mb-4">
							定番の背景除去サービスです。人物写真の精度が高く、髪の毛の細部まで丁寧に処理できます。
							月50枚まで無料（低解像度）、高解像度は有料プランが必要です。
						</p>

						<h3 className="text-lg font-semibold mt-8 mb-3">
							方法3：GIMP（手動）
						</h3>
						<p className="text-gray-700 mb-4">
							無料の画像編集ソフトGIMPを使い、「ファジー選択」で背景色を選択→削除することで透過にできます。
							単色の背景であれば比較的簡単に作業できます。複雑な背景は時間がかかります。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							【PowerPoint編】背景を透明にする方法
						</h2>
						<p className="text-gray-700 mb-4">
							PowerPointには「背景の削除」と「色の設定（透明色の指定）」の2種類の透過機能が組み込まれています。
						</p>

						<h3 className="text-lg font-semibold mt-6 mb-3">
							方法1：PowerPointの「背景の削除」機能
						</h3>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ol className="list-decimal list-inside space-y-2">
								<li>スライドに画像を挿入する</li>
								<li>画像を選択した状態で「書式」タブを開く</li>
								<li>「背景の削除」をクリック</li>
								<li>紫色で表示された削除範囲を調整</li>
								<li>「変更を保持」で確定</li>
							</ol>
						</div>
						<p className="text-gray-700 mb-4">
							単純な背景には有効ですが、複雑な写真では精度が低い場合があります。
							あらかじめしろぬきやで背景を透明にしたPNGをPowerPointに貼り付けると、より綺麗に仕上がります。
						</p>

						<h3 className="text-lg font-semibold mt-6 mb-3">
							方法2：透明色の指定
						</h3>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ol className="list-decimal list-inside space-y-2">
								<li>画像を選択→「書式」タブ→「色」→「透明色の設定」</li>
								<li>透明にしたい色の部分をクリック</li>
								<li>その色が透明になる</li>
							</ol>
						</div>
						<p className="text-gray-700 mb-4">
							「透明色の指定」はクリックした1色だけを透明にする機能です。
							単色の白背景ロゴ画像などには向きますが、グラデーション背景には対応できません。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							【動画編】背景を透明にする方法
						</h2>
						<p className="text-gray-700 mb-4">
							動画の背景を透明にする（除去する）方法は、画像とは異なります。
							グリーンバック（クロマキー）撮影か、AI背景除去ソフトを使うのが主な手段です。
						</p>

						<h3 className="text-lg font-semibold mt-6 mb-3">
							方法1：グリーンバック（クロマキー）
						</h3>
						<p className="text-gray-700 mb-4">
							緑色のバックスクリーンの前で撮影し、動画編集ソフト（Premiere
							Pro・DaVinci Resolve等）でグリーンを透明化します。
							精度が高く、プロの現場でも使われる定番手法です。
						</p>

						<h3 className="text-lg font-semibold mt-6 mb-3">
							方法2：AI動画背景除去ツール
						</h3>
						<p className="text-gray-700 mb-4">
							RunwayやCapCutなどのAIツールが、グリーンバックなしで動画の背景を自動除去できます。
							精度はグリーンバックより劣る場合がありますが、手軽さでは勝ります。
						</p>

						<div className="bg-yellow-50 rounded-xl p-4 mb-4 text-sm text-gray-700">
							<p className="font-semibold mb-1">注意点</p>
							<p>
								動画の背景透過には専用の動画編集ソフトが必要です。
								しろぬきやは静止画専用ツールのため、動画の背景除去には対応していません。
							</p>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							まとめ：用途別の最適手段
						</h2>
						<div className="space-y-3 text-sm text-gray-700">
							<div className="border-l-4 border-indigo-400 pl-4">
								<p className="font-semibold mb-1">
									商品写真・ロゴなど静止画の透過
								</p>
								<p>→ しろぬきや（AI自動・無料・登録不要）</p>
							</div>
							<div className="border-l-4 border-gray-400 pl-4">
								<p className="font-semibold mb-1">PowerPointでの資料作成</p>
								<p>→ しろぬきやで透過PNG作成→PowerPointに貼り付けが最も綺麗</p>
							</div>
							<div className="border-l-4 border-gray-400 pl-4">
								<p className="font-semibold mb-1">動画の背景除去</p>
								<p>
									→ グリーンバック＋動画編集ソフト、またはRunway等のAIツール
								</p>
							</div>
						</div>

						<p className="text-gray-700 mt-6">
							静止画の背景透過であれば、しろぬきやが最もスムーズです。
							登録不要・無料で今すぐ試せます。
						</p>

						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
